import { NgIf } from '@angular/common';
import {
  ChangeDetectorRef,
  Component,
  ElementRef,
  EventEmitter,
  Input,
  OnInit,
  Output,
  ViewChild,
  ViewEncapsulation
} from '@angular/core';
import { VideoErrorsConfig } from '../../interfaces';
import {
  AppConstantService,
  CommonBindingDataService,
  NotificationService
} from '@Shared/services';
import { BusinessService, RestApiService } from '@App/common/services';
import { AppSettings } from '@Shared/app.settings';
import { HttpHeaders } from '@angular/common/http';
import { LoaderService } from '@Shared/components/loader/loader.service';
import { ProgressBarModule } from 'primeng/progressbar';
import { TranslateModule } from '@ngx-translate/core';
import { FileUploadModule } from 'primeng/fileupload';

declare const Hls: any;

@Component({
  selector: 'app-video-upload',
  templateUrl: './video-upload.component.html',
  standalone: true,
  imports: [FileUploadModule, TranslateModule, ProgressBarModule, NgIf],
  encapsulation: ViewEncapsulation.None
})
export class VideoUploadComponent implements OnInit {
  uploadedFiles: any[] = [];
  progressValue = 0;
  @Input() videoUploadText = true;
  @Input() afterVideoUploadText;
  @Input() chooseIcon: string = 'pi-plus';
  @Input() maxDuration: number;
  @Input() fileLimit: number = 1;
  @Input() errorConfig: VideoErrorsConfig;
  videoFormat;
  fileUploadUrl = AppSettings.UPLOAD_FILE_URL + '?fileType=1';
  isUploading = false;
  isContinueUploading = false;
  isUploaded = false;
  isDurationChecked = false;
  hls;
  maxSizeVideo = AppSettings.VIDEO_UPLOAD_MAX_SIZE_IN_BYTE;
  headers: HttpHeaders;
  meterVideoConfig: { videoEl: any; videoUrl: any; mute: boolean } = {
    videoEl: null,
    videoUrl: '',
    mute: true
  };
  durationMeterHls;
  videoName: string = '';
  formatsWithNoMeta = ['.avi', '.wmv', '.flv', '.vob', '.mpg'];

  @Output() preUpload: EventEmitter<any> = new EventEmitter();
  @Output() postUpload: EventEmitter<any> = new EventEmitter();
  @Output() errorInVideoUpload: EventEmitter<any> = new EventEmitter();

  @ViewChild('durationMeter', { static: false }) durationMeter: ElementRef;

  constructor(
    private notificationService: NotificationService,
    private appConstantService: AppConstantService,
    private restApiService: RestApiService,
    private commonBindingService: CommonBindingDataService,
    private cdr: ChangeDetectorRef,
    private businessService: BusinessService,
    private loaderService: LoaderService
  ) {}

  ngOnInit() {
    this.videoFormat = AppSettings.VIDEO_FORMAT;
    this.headers = this.restApiService.getHeadersFileUpload();
  }

  onSelect(event) {
    const fileExtension = event.files[0].name.substring(
      event.files[0].name.lastIndexOf('.')
    );
    const isValidExtension = this.appConstantService.video_extensions.find(
      (x) => x.toLowerCase() === fileExtension.toLowerCase()
    );
    this.isDurationChecked = false;
    this.errorInVideoUpload.emit(null);

    this.videoName = event.files[0].name;
    if (!isValidExtension) {
      this.isUploading = false;
      this.notificationService.showNotification(
        'ERROR',
        this.commonBindingService.getLabel(
          'lbl_please_select_valid_video_format'
        )
      );
      return;
    }

    if (event.files[0]) {
      const type = event.files[0].type;
      if (!type.includes('video/')) {
        this.isUploading = false;
        this.notificationService.showNotification(
          'ERROR',
          this.commonBindingService.getLabel(
            'lbl_please_select_valid_video_format'
          )
        );
        return;
      }
    }
    if (this.maxSizeVideo < event.files[0].size) {
      this.isUploading = false;
      this.notificationService.showNotification(
        'ERROR',
        'File size exceeds the maximum limit of 500MB. Please choose a smaller file.'
      );
      return;
    }

    if (!this.maxDuration) {
      if (event.files[0].name) {
        this.preUpload.emit(event.files[0].name);
      }
    } else {
      this.durationMeter.nativeElement.src = URL.createObjectURL(
        event.files[0]
      );
      if (this.formatsWithNoMeta.find((f) => f === isValidExtension)) {
        this.meterVideoConfig = {
          videoEl: this.durationMeter,
          videoUrl: '',
          mute: true
        };
      }
    }
  }

  playDurationMeterVideo(config) {
    this.cdr.detectChanges();
    const videoFileUrl = config.videoUrl;

    if (config.videoEl) {
      config.videoEl.nativeElement.muted = config.mute;

      if (videoFileUrl) {
        if (Hls.isSupported()) {
          if (this.durationMeterHls) {
            this.durationMeterHls.destroy();
          }

          this.durationMeterHls = new Hls();
          this.durationMeterHls.loadSource(videoFileUrl);
          this.durationMeterHls.attachMedia(config.videoEl.nativeElement);
          config.videoEl.nativeElement.play();
        } else if (
          config.videoEl.nativeElement.canPlayType(
            'application/vnd.apple.mpegurl'
          )
        ) {
          config.videoEl.nativeElement.src = videoFileUrl;
          config.videoEl.nativeElement.play();
        }
      }
    }
  }

  onUpload(event) {
    const isDurationChecked = this.maxDuration ? this.isDurationChecked : true;
    this.uploadedFiles = event.originalEvent.body;
    if (isDurationChecked) {
      this.postUploading();
    } else {
      this.isContinueUploading = true;
      this.isUploading = true;
      this.loaderService.show();
      if (this.uploadedFiles[0]?.fileId) {
        this.pollMuxAPI(4000);
      } else {
        this.loaderService.hide();
        this.notificationService.showNotification(
          'ERROR',
          this.commonBindingService.getLabel('msg_video_details_fetch_error')
        );
      }
    }
  }

  pollMuxAPI(time) {
    setTimeout(() => {
      this.fetchMuxVideoData();
    }, time);
  }

  fetchMuxVideoData() {
    this.businessService.getVideoDetail(this.uploadedFiles[0].fileId).subscribe(
      (result) => {
        if (result?.data && result?.data?.status !== 'errored') {
          if (result?.data?.status !== 'ready') {
            this.pollMuxAPI(4000);
          } else if (this.meterVideoConfig) {
            this.meterVideoConfig.videoUrl = result.data.videoUrl;
            this.playDurationMeterVideo(this.meterVideoConfig);
          }
        } else {
          if (this.isUploading) {
            this.notificationService.showNotification(
              'ERROR',
              this.commonBindingService.getLabel(
                'msg_video_details_fetch_error'
              )
            );
          }
          this.isUploaded = false;
          this.isUploading = false;
          this.isContinueUploading = false;
        }
        this.loaderService.hide();
      },
      () => {
        this.loaderService.hide();
        this.notificationService.showNotification(
          'ERROR',
          this.commonBindingService.getLabel('msg_video_details_fetch_error')
        );
      }
    );
  }

  onBeforeUpload() {
    this.isUploading = true;
  }

  onProgress(event: { progress: number }) {
    this.progressValue = event.progress;
  }

  onError() {
    this.errorInVideoUpload.emit(
      this.commonBindingService.getLabel('lbl_error_in_upload_try_again')
    );
    this.progressValue = 0;
    this.isUploading = false;
    this.isContinueUploading = false;
  }

  checkDuration(event: any) {
    if (!Number.isNaN(event.target.duration)) {
      this.isDurationChecked = true;
      if (Math.floor(event.target.duration) > this.maxDuration) {
        if (this.isContinueUploading) {
          this.isUploading = false;
          this.isUploaded = false;
        }
        this.isContinueUploading = false;
        this.progressValue = 0;
        return;
      } else if (
        this.meterVideoConfig &&
        Math.floor(event.target.duration) < this.maxDuration
      ) {
        if (this.durationMeterHls) {
          this.durationMeterHls.destroy();
        }
        // this.postUploading();
      }
    }
  }

  postUploading() {
    this.isUploading = false;
    this.isUploaded = true;
    this.postUpload.emit({ uploadedFiles: this.uploadedFiles });
    this.durationMeter.nativeElement.src = '';
    this.meterVideoConfig.videoUrl = '';
    this.videoName = '';
  }
}
